/**
 * Выгрузка отчёта в файл, который открывается в Excel.
 *
 * Файл собирается в браузере, а не на сервере, и это осознанно. Данные для
 * него уже загружены — это те самые строки, что видны на экране; язык, часовой
 * пояс и правила форматирования тоже известны только здесь. Собирать то же
 * самое на сервере значило бы завести там вторую копию словарей и вторую
 * копию перевода единиц — и однажды они разойдутся.
 *
 * Формат — CSV, а не .xlsx. С меткой UTF-8 и правильным разделителем он
 * открывается двойным щелчком в любой версии Excel, в Numbers и в LibreOffice,
 * не требует ни библиотеки, ни зависимости и не ломается на больших треках.
 * Если понадобятся листы, ширины колонок и заголовки — тогда и появится .xlsx.
 */

import type { EventRow, Position, StopRow, SummaryRow, TripRow } from './api'
import { EXCEL_LOCALE, toCsv } from './csv'
import { eventText } from './eventName'
import { dateTime, hoursDecimal, km, kmh, num } from './format'
import { dict, type Dict, type Lang } from './i18n'
import { believable } from './summary'
import { energyLevel, fuelUsedLiters, isBlocked } from './vehicle'

/** Отчёты, которые можно выгрузить. Порядок — как в интерфейсе. */
export const EXPORTABLE = ['trips', 'stops', 'summary', 'events', 'route'] as const

export type ExportKind = (typeof EXPORTABLE)[number]

export type ExportContext = {
  lang: Lang
  /** Имена машин по номеру: события и точки трека приходят без названия. */
  nameById: Map<number, string>
  /**
   * Текущие позиции машин. Нужны сводке: накопительный расход с установки
   * (AVL 107) отчёт не несёт, он лежит в последней позиции.
   */
  positions?: Map<number, Position>
}

type Sheet = { header: string[]; rows: string[][] }

/** Собирает текст CSV-файла по строкам отчёта. */
export function reportCsv(kind: ExportKind, rows: unknown[], ctx: ExportContext): string {
  const t = dict(ctx.lang)
  const sheet = SHEETS[kind](rows, ctx, t)
  return toCsv(sheet.header, sheet.rows, EXCEL_LOCALE[ctx.lang])
}

/** Имя машины: из самого отчёта, иначе из списка, иначе номером. */
function vehicle(row: { deviceId: number; deviceName?: string }, ctx: ExportContext): string {
  return row.deviceName || ctx.nameById.get(row.deviceId) || `#${row.deviceId}`
}

/** Поездки и стоянки Traccar отдаёт с длительностью; на всякий случай считаем сами. */
function spanMs(row: { duration?: number; startTime: string; endTime: string }): number {
  if (typeof row.duration === 'number') return row.duration
  return Date.parse(row.endTime) - Date.parse(row.startTime)
}

/** Без разрядов, как и остальные числа выгрузки: Excel считает их текстом. */
function fuelUsedCell(position: Position | undefined, lang: Lang): string {
  const liters = fuelUsedLiters(position)
  return liters === null ? '' : num(liters, lang, { grouping: false })
}

const SHEETS: Record<
  ExportKind,
  (rows: unknown[], ctx: ExportContext, t: Dict) => Sheet
> = {
  trips: (rows, ctx, t) => ({
    header: [
      t.csv.vehicle,
      t.csv.start,
      t.csv.end,
      t.csv.duration,
      t.csv.distance,
      t.csv.avgSpeed,
      t.csv.maxSpeed,
      t.csv.addressFrom,
      t.csv.addressTo,
    ],
    rows: (rows as TripRow[]).map((row) => [
      vehicle(row, ctx),
      dateTime(row.startTime, ctx.lang, true),
      dateTime(row.endTime, ctx.lang, true),
      hoursDecimal(spanMs(row), ctx.lang),
      km(row.distance, ctx.lang, false),
      believable(row, 'averageSpeed') ? kmh(row.averageSpeed, ctx.lang) : '',
      believable(row, 'maxSpeed') ? kmh(row.maxSpeed, ctx.lang) : '',
      row.startAddress ?? '',
      row.endAddress ?? '',
    ]),
  }),

  stops: (rows, ctx, t) => ({
    header: [t.csv.vehicle, t.csv.start, t.csv.end, t.csv.duration, t.csv.address],
    rows: (rows as StopRow[]).map((row) => [
      vehicle(row, ctx),
      dateTime(row.startTime, ctx.lang, true),
      dateTime(row.endTime, ctx.lang, true),
      hoursDecimal(spanMs(row), ctx.lang),
      row.address ?? '',
    ]),
  }),

  summary: (rows, ctx, t) => ({
    header: [
      t.csv.vehicle,
      t.csv.distance,
      t.csv.avgSpeed,
      t.csv.maxSpeed,
      t.csv.engineHours,
      t.csv.fuelUsed,
    ],
    rows: (rows as SummaryRow[]).map((row) => [
      vehicle(row, ctx),
      km(row.distance, ctx.lang, false),
      // Непосчитанное значение выгружается пустой ячейкой, а не нулём: ноль в
      // таблице участвует в подсчётах и портит средние.
      believable(row, 'averageSpeed') ? kmh(row.averageSpeed, ctx.lang) : '',
      believable(row, 'maxSpeed') ? kmh(row.maxSpeed, ctx.lang) : '',
      believable(row, 'engineHours') ? hoursDecimal(row.engineHours, ctx.lang) : '',
      fuelUsedCell(ctx.positions?.get(row.deviceId), ctx.lang),
    ]),
  }),

  events: (rows, ctx, t) => ({
    header: [t.csv.vehicle, t.csv.time, t.csv.event],
    rows: (rows as EventRow[]).map((row) => [
      vehicle(row, ctx),
      dateTime(row.eventTime, ctx.lang, true),
      // Незнакомый тип выгружается как есть: скрыть его хуже, чем показать
      // непереведённым — иначе события просто исчезнут из отчёта.
      eventText(row, t),
    ]),
  }),

  route: (rows, ctx, t) => ({
    header: [
      t.csv.vehicle,
      t.csv.time,
      t.csv.latitude,
      t.csv.longitude,
      t.csv.speed,
      t.csv.ignition,
      t.csv.relay,
      t.csv.energy,
    ],
    rows: (rows as Position[]).map((point) => [
      vehicle(point, ctx),
      dateTime(point.deviceTime, ctx.lang, true),
      num(point.latitude, ctx.lang, { max: 6, grouping: false }),
      num(point.longitude, ctx.lang, { max: 6, grouping: false }),
      kmh(point.speed, ctx.lang),
      ignitionText(point, t),
      isBlocked(point) ? t.relayRaised : t.relayClear,
      energyText(point, ctx),
    ]),
  }),
}

/**
 * Уровень топлива или заряда для трека.
 *
 * Когда параметра нет, ячейка остаётся пустой, а не заполняется прочерком:
 * колонка числовая, и таблица должна уметь посчитать по ней среднее. Прочерк
 * превратил бы весь столбец в текст.
 */
function energyText(point: Position, ctx: ExportContext): string {
  const energy = energyLevel(point)
  return energy ? num(energy.percent, ctx.lang, { grouping: false }) : ''
}

function ignitionText(point: Position, t: Dict): string {
  const raw = point.attributes?.ignition
  if (typeof raw !== 'boolean') return t.unknown
  return raw ? t.on : t.off
}

/** Оставляет от имени машины только то, что безопасно в имени файла. */
function slug(text: string): string {
  return text
    .trim()
    .replace(/[^\p{L}\p{N}_-]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

/**
 * Имя сохраняемого файла: вид отчёта, машина и обе даты.
 *
 * Имя машины задаёт человек и оно попадает сюда как есть, поэтому всё, кроме
 * букв и цифр, вычищается: имени с косой чертой в имени файла делать нечего.
 */
export function reportFilename(
  kind: ExportKind,
  since: Date,
  until: Date,
  device?: string,
): string {
  const day = (moment: Date) => moment.toISOString().slice(0, 10)
  const parts = [kind, device ? slug(device) : '', `${day(since)}_${day(until)}`]
  return parts.filter(Boolean).join('-') + '.csv'
}

/**
 * Отдаёт файл браузеру.
 *
 * Скачивание идёт из памяти, а не ссылкой на сервер: так ошибка выгрузки
 * остаётся внутри приложения и показывается человеку, а не уводит его на
 * страницу с сырым ответом сервера.
 */
export function downloadCsv(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Ссылку освобождаем с запасом: Safari начинает скачивание не мгновенно.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
